import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { DeviceManagementService } from '@services/device-management.service';
import { StorageService } from './storage.service';
import { EventManagerService } from './event-manager.service';
import { APIGatewayService } from './api-gateway.service';
import { AuthService } from './auth.service';
import { ComponentPageService } from '@services/component-pages.service';
import { AppLoggerService } from '@services/app-logger.service';
import { defaultConfig } from 'src/app/app.config';

export interface SessionData {
  sessionId: string;
  expireOn?: string;
  sessionKeys?: {
    signKeyCrypto?: string;
    encryptKeyCrypto?: string;
  };
  /** true si el cliente tuvo productos pero todos están cerrados ('C'); ver HU Certificados Clientes Antiguos */
  certificatesOnlyAccess?: boolean;
  [key: string]: any;
}

export interface UserOptions {
  userFunctions?: string[];
  [key: string]: any;
}

export interface MenuItem {
  name: string;
  functions?: string[];
  children?: MenuItem[];
  [key: string]: any;
}

export interface AppMenus {
  [menuName: string]: MenuItem[];
}

const {
  formActivity,
  keepTheSessionAlive,
  menuObtained,
  noSessionNext,
  sessionEncryptKeyReceived,
  sessionEnded,
  sessionReplaced,
  sessionEstablished,
  sessionObtained,
  sessionSignKeyReceived,
  sessionToBeClosed,
  sessionEndSchedule,
  sessionRefreshSchedule,
  sessionLastActivity,
} = defaultConfig.appEvents;

const { sessionToBeClosedWarning } = defaultConfig;

const SESSION_ID = 'sessionId';
// Marcador normalizado de sesión cerrada/expirada que estampa el gateway (api-response.js).
const SESSION_CLOSED_ERROR_TYPE = 'SESSION_CLOSED';

export const activeSessionErrors = Object.freeze({
  validSession: 0,
  expiredOnServer: 1,
  concurrentSession: 2,
  missingUserFunctions: 3,
  noSession: 4,
});

@Injectable({
  providedIn: 'root',
})
export class UserSessionService {
  private _eventManager = inject(EventManagerService);
  private _apiGateway = inject(APIGatewayService);
  private _authService = inject(AuthService);
  private _storageService = inject(StorageService);
  private _componentPageService = inject(ComponentPageService);
  private _deviceManagementService = inject(DeviceManagementService);
  private _logger = inject(AppLoggerService);

  sessionData: SessionData | null = null;
  appMenus: AppMenus | null;
  userOptions: UserOptions | null;
  lastRequestedForm: string | string[] = '';
  lastAppActivityDate: Date = new Date();
  lastSessionRefresh: Date = new Date();
  lastSessionRecovery: Date = null;
  sessionExpirationDate: Date = null;
  sessionEndWarningTimer: ReturnType<typeof setTimeout>;
  sessionEndTimer: ReturnType<typeof setTimeout>;
  sessionKeepAliveTimer: ReturnType<typeof setTimeout>;
  customSessionData: Record<string, any> = {};

  // Duración total de la sesión inferida del backend (máximo `remaining` observado en una
  // renovación fresca). Se usa para calcular la vida media y disparar renovación por actividad.
  sessionDurationMs: number = null;
  private _keepAliveInFlight = false;

  // Reintentos ante fallo transitorio del keep-alive: NO se cierra la sesión por un ping fallido;
  // se reintenta en un backoff corto y solo se cierra ante expiración real o señal SESSION_CLOSED.
  private _keepAliveRetryTimer: ReturnType<typeof setTimeout> | null = null;
  private _keepAliveFailures = 0;
  private static readonly KEEPALIVE_MAX_RETRIES = 3;
  private static readonly KEEPALIVE_RETRY_MS = 5000;

  private _activityThrottleTimer: ReturnType<typeof setTimeout> | null = null;
  private static readonly ACTIVITY_THROTTLE_MS = 1000;
  private static readonly DOM_ACTIVITY_EVENTS = ['click', 'keydown', 'scroll', 'touchstart'] as const;

  constructor() {
    this.startSessionService();
  }

  startSessionService() {
    this.resetCustomSessionData();
    this._eventManager.subscribe(sessionObtained.name, data => this.completeSessionEstablish(data));
    this._eventManager.subscribe(sessionEnded.name, data => this.clearService(data?.nextForm));
    this._eventManager.subscribe(noSessionNext.name, route => this.setRequestedRoute(route));
    this._eventManager.subscribe(formActivity.name, () => this.updateLastAppActivity());
    this._eventManager.subscribe(keepTheSessionAlive.name, update => this.keepSessionAlive(update));
    this.startDomActivityListener();
  }

  private startDomActivityListener() {
    if (typeof document === 'undefined') {
      return;
    }
    const handler = () => this.notifyDomActivity();
    for (const evt of UserSessionService.DOM_ACTIVITY_EVENTS) {
      document.addEventListener(evt, handler, { passive: true, capture: true });
    }
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.notifyDomActivity();
        // HU 198971 — al volver la pestaña al frente se verifica la sesión de inmediato. El push SSE
        // `sessionReplaced` se pierde si otro dispositivo tomó la sesión mientras esta estaba oculta
        // (el SSE se cierra y no tiene replay), dejando el equipo viejo abierto hasta la siguiente
        // renovación. keepSessionAlive consulta el estado autoritativo (UserActiveDevices) y lo cierra.
        if (this.sessionData) {
          this.keepSessionAlive(false);
        }
      }
    });
  }

  private notifyDomActivity() {
    if (!this.sessionData || this._activityThrottleTimer) {
      return;
    }
    this._eventManager.next(formActivity.name, null);
    this._activityThrottleTimer = setTimeout(() => {
      this._activityThrottleTimer = null;
    }, UserSessionService.ACTIVITY_THROTTLE_MS);
  }

  private logSessionTrace(message: string) {
    this._logger.log(`[UserSession][keepSessionAlive] ${new Date().toISOString()} ${message}`);
  }

  resetCustomSessionData() {
    this.customSessionData = {};
  }

  addCustomSessionData(name, value) {
    this.customSessionData[name] = value;
  }

  delCustomSessionData(name) {
    delete this.customSessionData?.[name];
  }

  getCustomSessionData(name) {
    return this.customSessionData?.[name] ?? null;
  }

  clearService(nextForm = null) {
    this.sessionKeepAliveTimer && clearTimeout(this.sessionKeepAliveTimer);
    this.sessionEndWarningTimer && clearTimeout(this.sessionEndWarningTimer);
    this.sessionEndTimer && clearTimeout(this.sessionEndTimer);
    this.resetKeepAliveRetry();
    this.sessionData = null;
    this.appMenus = null;
    this.userOptions = null;
    this.sessionExpirationDate = null;
    this.sessionDurationMs = null;
    this.sessionKeepAliveTimer = null;
    this.clearStoredSessionId();
    if (nextForm && !this.lastRequestedForm) {
      this.lastRequestedForm = nextForm;
    }
  }

  setSessionExpirationDate(expireOn) {
    if (!expireOn) {
      return; // this._eventManager.next(sessionEnded.name, null)
    }
    // F3: comparar por VALOR de tiempo. Antes se comparaban dos objetos Date por referencia
    // (`this.sessionExpirationDate !== previousExpirationDate`), así que `timersMustChange` era
    // SIEMPRE true y los timers se destruían/recreaban en cada llamada aunque el expireOn no cambiara.
    const previousExpirationMs = this.sessionExpirationDate ? this.sessionExpirationDate.getTime() : null;
    const newExpirationDate = new Date(expireOn);
    const newExpirationMs = newExpirationDate.getTime();
    const timersMustChange = newExpirationMs !== previousExpirationMs;
    this.sessionExpirationDate = newExpirationDate;
    const currentDate = new Date();
    const remainingSessionTime = newExpirationMs - currentDate.getTime();
    // El mayor `remaining` visto en una renovación fresca ≈ duración total concedida por el backend.
    this.sessionDurationMs = Math.max(this.sessionDurationMs ?? 0, remainingSessionTime);
    if (timersMustChange) {
      const warningTime = remainingSessionTime - sessionToBeClosedWarning;
      if (warningTime > 0) {
        this.sessionEndWarningTimer && clearTimeout(this.sessionEndWarningTimer);
        this.sessionEndWarningTimer = setTimeout(() => this.onSessionEndWarning(), warningTime);
      }
      this.updateRefreshTimer();
      const endTimerModified = !!this.sessionEndTimer;
      this.sessionEndTimer && clearTimeout(this.sessionEndTimer);
      this._eventManager.next(sessionEndSchedule.name, remainingSessionTime);
      this.logSessionTrace(
        `temporizador de fin de sesión ${endTimerModified ? 'modificado' : 'creado'} — ` +
          `disparará keepSessionAlive en ${Math.round(remainingSessionTime / 1000)}s ` +
          `(expira ${this.sessionExpirationDate.toISOString()})`
      );
      this.sessionEndTimer = setTimeout(() => {
        this.logSessionTrace('se cumplió el temporizador de fin de sesión → ejecutando keepSessionAlive(false)');
        this.keepSessionAlive(false);
      }, remainingSessionTime);
    }
  }

  /**
   * F7: el aviso de cierre (`sessionToBeClosed`) es por INACTIVIDAD. Si hubo actividad desde la
   * última renovación efectiva, renovamos en silencio en vez de mostrar el modal — así un usuario
   * activo no ve avisos espurios. Solo se avisa cuando de verdad no hubo actividad.
   */
  private onSessionEndWarning() {
    if (!this.sessionData) {
      return;
    }
    if (this.lastAppActivityDate > this.lastSessionRefresh) {
      this.logSessionTrace('aviso de cierre con actividad reciente → se renueva en vez de avisar');
      this.keepSessionAlive(false);
      return;
    }
    this._eventManager.next(sessionToBeClosed.name, 'closeSession');
  }

  setRequestedRoute(nextRoute) {
    if (nextRoute) {
      const [subject] = nextRoute.split('/').splice(-1);
      this.lastRequestedForm = [this._componentPageService.routeForm(nextRoute), subject];
    }
  }

  async updateLastAppActivity() {
    if (this.sessionData) {
      this.lastAppActivityDate = new Date();
      this._eventManager.next(sessionLastActivity.name, this.lastAppActivityDate);
      this.renewOnActivityIfNeeded();
    }
  }

  /**
   * Renueva la sesión de inmediato cuando hay actividad y el tiempo restante ya bajó de la
   * vida media de la sesión (p. ej. sesión de 20 min → renueva al actuar con < 10 min restantes).
   * Se auto-regula: tras renovar, `remaining` vuelve a ~la duración total y la condición deja de
   * cumplirse hasta cruzar de nuevo la mitad. El guard `_keepAliveInFlight` evita llamados concurrentes.
   */
  private renewOnActivityIfNeeded() {
    if (!this.sessionData || !this.sessionExpirationDate || this._keepAliveInFlight) {
      return;
    }
    const remaining = this.sessionExpirationDate.getTime() - new Date().getTime();
    const halfLife = (this.sessionDurationMs ?? 0) / 2;
    if (halfLife > 0 && remaining < halfLife) {
      this.logSessionTrace(
        `actividad con tiempo restante (${Math.round(remaining / 1000)}s) < vida media ` +
          `(${Math.round(halfLife / 1000)}s) → renovación inmediata por actividad`
      );
      // F2: NO adelantar lastSessionRefresh aquí. Antes se seteaba antes de keepSessionAlive, que
      // puede ser no-op por el guard _keepAliveInFlight; entonces lastSessionRefresh avanzaba sin
      // renovación real y el siguiente lastAppActivityCheck veía hadActivity=false y saltaba la
      // renovación. Ahora lastSessionRefresh solo se mueve dentro de keepSessionAlive al renovar.
      this.keepSessionAlive(false);
    }
  }

  async updateRefreshTimer() {
    const currentDate = new Date();
    const keepAliveTimerModified = !!this.sessionKeepAliveTimer;
    this.sessionKeepAliveTimer && clearTimeout(this.sessionKeepAliveTimer);
    const halfRemainingSessionTime = (this.sessionExpirationDate.getTime() - currentDate.getTime()) / 2;
    const nextRefreshTime = Math.max(halfRemainingSessionTime, sessionToBeClosedWarning);
    this._eventManager.next(sessionRefreshSchedule.name, nextRefreshTime);
    this.logSessionTrace(
      `temporizador keep-alive ${keepAliveTimerModified ? 'modificado' : 'creado'} — ` +
        `próxima verificación en ${Math.round(nextRefreshTime / 1000)}s ` +
        `(${new Date(currentDate.getTime() + nextRefreshTime).toISOString()})`
    );
    this.sessionKeepAliveTimer = setTimeout(() => this.lastAppActivityCheck(), nextRefreshTime);
  }

  async lastAppActivityCheck() {
    if (!this.sessionData) {
      return;
    }
    const hadActivity = this.lastAppActivityDate > this.lastSessionRefresh;
    this.logSessionTrace(
      `se cumplió el temporizador keep-alive → hubo actividad reciente: ${hadActivity} ` +
        `(${hadActivity ? 'se ejecutará keepSessionAlive' : 'no se renueva'})`
    );
    if (hadActivity) {
      // F2: lastSessionRefresh lo mueve keepSessionAlive solo al renovar de verdad (no aquí, que
      // adelantaba el marcador aunque la renovación fuera no-op).
      this.keepSessionAlive(false);
    }
    this.updateRefreshTimer();
  }

  async keepSessionAlive(updateLastActivity = false) {
    if (!this.sessionData || this._keepAliveInFlight) {
      return; // sin sesión, o ya hay un keep-alive en curso (evita llamados concurrentes)
    }
    this._keepAliveInFlight = true;
    try {
      updateLastActivity && this.updateLastAppActivity();

      // 1. Extend backend session
      this.logSessionTrace(
        `ejecutando llamado al servicio _apiGateway.keepSessionAlive ` +
          `(updateLastActivity=${updateLastActivity}, lastAppActivityDate=${this.lastAppActivityDate.toISOString()})`
      );
      const response = await this._apiGateway.keepSessionAlive(this.lastAppActivityDate);

      // HU 198971: el gateway responde SESSION_REPLACED cuando esta sesión fue reemplazada por un
      // login en otro dispositivo. Se cierra el equipo viejo con el mismo flujo que el evento SSE
      // (que puede no llegar si el SSE estaba caído en segundo plano). No es un fallo transitorio.
      if (response?.getErrorCode?.() === 'SESSION_REPLACED') {
        this.resetKeepAliveRetry();
        return this._eventManager.next(sessionReplaced.name, null);
      }

      const expireOn = response?.data?.['expireOn'] ?? null;

      if (!expireOn) {
        // Sin expireOn. Si es la señal definitiva SESSION_CLOSED, el cierre ya lo dispara
        // APIGatewayService.detectSessionClosed (modal → sessionEnded): ni reintentamos ni cerramos.
        if (response?.error?.errorType === SESSION_CLOSED_ERROR_TYPE) {
          return;
        }
        // Fallo transitorio (red/hipo del backend): NO cerrar. Se conserva la expiración actual y se
        // reintenta en un backoff corto. Si la sesión está realmente vencida, otra petición
        // (getInfo → SESSION_CLOSED) o el propio reintento lo resolverá; nunca cerramos por un ping
        // fallido, que era la causa de cierres con actividad reciente.
        this.scheduleKeepAliveRetry();
        return;
      }

      // Renovación efectiva: limpiar el estado de reintentos.
      this.resetKeepAliveRetry();

      const remainingSessionTime = new Date(expireOn as string).getTime() - new Date().getTime();
      if (remainingSessionTime < sessionToBeClosedWarning) {
        // Expiración real inminente confirmada por el backend → cerrar.
        return this._eventManager.next(sessionEnded.name, null);
      }
      this.setSessionExpirationDate(expireOn);
      // F2: marca de última renovación EFECTIVA (solo aquí, tras obtener un expireOn válido). La usan
      // lastAppActivityCheck y onSessionEndWarning para saber si hubo actividad desde la renovación.
      this.lastSessionRefresh = new Date();

      // 2. Refresh OAuth2 tokens solo si están por vencer. Desacopla el refresh de token
      // del keep-alive: el ping de sesión ya no arrastra un POST a /oauth2/token en cada
      // renovación. El interceptor de 401 sigue siendo la red de seguridad reactiva.
      const tokenRefreshed = await firstValueFrom(this._authService.refreshAccessTokenIfNeeded());
      if (!tokenRefreshed) {
        // Si Kratos sigue viva, intentamos un renew silencioso vía prompt=none antes de cerrar.
        const silentRenewStarted = await this._authService.attemptSilentRenew();
        if (!silentRenewStarted) {
          return this._eventManager.next(sessionEnded.name, null);
        }
        return;
      }
    } finally {
      this._keepAliveInFlight = false;
    }
  }

  /**
   * Programa un reintento del keep-alive tras un fallo transitorio, con un tope de reintentos
   * inmediatos. No cierra la sesión: si el corte persiste, los timers normales (refresh/fin) y la
   * próxima actividad/petición (getInfo → SESSION_CLOSED) resolverán el estado real.
   */
  private scheduleKeepAliveRetry() {
    this._keepAliveFailures += 1;
    this._keepAliveRetryTimer && clearTimeout(this._keepAliveRetryTimer);
    this._keepAliveRetryTimer = null;
    this.logSessionTrace(`keepAlive falló (transitorio) intento ${this._keepAliveFailures}; NO se cierra la sesión`);
    if (this._keepAliveFailures > UserSessionService.KEEPALIVE_MAX_RETRIES) {
      return; // agotados los reintentos inmediatos; se resolverá por actividad/petición
    }
    this._keepAliveRetryTimer = setTimeout(() => {
      this._keepAliveRetryTimer = null;
      this.keepSessionAlive(false);
    }, UserSessionService.KEEPALIVE_RETRY_MS);
  }

  private resetKeepAliveRetry() {
    this._keepAliveFailures = 0;
    this._keepAliveRetryTimer && clearTimeout(this._keepAliveRetryTimer);
    this._keepAliveRetryTimer = null;
  }

  async activeSession() {
    const currentSessionInApp =
      this.sessionData && this.userOptions?.userFunctions ? this.sessionData?.sessionId : null;
    let activeSessionResult: number = currentSessionInApp
      ? activeSessionErrors.validSession
      : activeSessionErrors.noSession;
    if (currentSessionInApp) {
      this.updateLastAppActivity();
    } else {
      this.lastSessionRecovery = new Date();
      this.resetCustomSessionData();
      const { data: serverSessionData } = await this._apiGateway.obtainCurrentSession();
      const { sessionId: currentSessionInServer, expireOn } = serverSessionData;
      this.setSessionExpirationDate(expireOn);
      this.storeNewSessionId(currentSessionInServer);
      if (!currentSessionInApp) {
        if (!currentSessionInServer) {
          this.clearStoredSessionId();
          activeSessionResult = activeSessionErrors.noSession;
        } else {
          this.resetCustomSessionData();
          activeSessionResult = await this.completeSessionEstablish(serverSessionData, !!currentSessionInApp, false);
        }
      } else {
        if (!currentSessionInServer) {
          this.clearStoredSessionId();
          activeSessionResult = activeSessionErrors.expiredOnServer;
        } else if (currentSessionInServer !== currentSessionInApp) {
          activeSessionResult = activeSessionErrors.concurrentSession;
        }
      }
    }
    return activeSessionResult;
  }

  async completeSessionEstablish(sessionData, currentlyOnSession = false, changePage = true) {
    if (!sessionData) {
      return activeSessionErrors.noSession;
    }
    const { expireOn } = sessionData;
    this.setSessionExpirationDate(expireOn);
    this.lastSessionRecovery = new Date();
    const signCryptogram = sessionData?.sessionKeys?.signKeyCrypto ?? null;
    const encryptKeyCryptogram = sessionData?.sessionKeys?.encryptKeyCrypto ?? null;
    if (signCryptogram && encryptKeyCryptogram) {
      const signKey = await this._deviceManagementService.decryptData(signCryptogram);
      const encryptKey = await this._deviceManagementService.decryptData(encryptKeyCryptogram);
      this._eventManager.next(sessionSignKeyReceived.name, signKey);
      this._eventManager.next(sessionEncryptKeyReceived.name, encryptKey);
    }
    this.storeNewSessionId(sessionData.sessionId);
    this.sessionData = sessionData;
    this.lastAppActivityCheck();
    if (!currentlyOnSession) {
      const [userOptionsResp, frontendMenusRes] = await Promise.all([
        this._apiGateway.getUserPrivileges(),
        this._apiGateway.getMenuData(),
      ]);
      const frontendMenus = frontendMenusRes.getData() as unknown as { name: string; items: MenuItem[] }[];
      this.userOptions = userOptionsResp.getData() as unknown as UserOptions;
      if (!this.userOptions) {
        return activeSessionErrors.missingUserFunctions;
      }
      const userFunctions = this.userOptions?.userFunctions ?? [];
      const fullSessionData = {
        changePage,
        sessionData,
        privileges: userFunctions,
        nextForm: this.lastRequestedForm,
      };
      this.storeNewSessionId(sessionData?.sessionId);
      this._eventManager.next(sessionEstablished.name, fullSessionData);
      // Se personalizan  los menus obtenidos
      this.appMenus = {};
      for (let index = 0; index < frontendMenus.length; index++) {
        const { name, items } = frontendMenus[index];
        this.appMenus[name] = this.filterMenuItems(items, userFunctions);
      }
      this._eventManager.next(menuObtained.name, this.appMenus);
      this.lastRequestedForm = '';
    }
    return activeSessionErrors.validSession;
  }

  async buildFakeSession(sessionData) {
    this.sessionData = sessionData;
    const fakeSessionExpiration = new Date();
    fakeSessionExpiration.setDate(fakeSessionExpiration.getDate() + 1);
    this.setSessionExpirationDate(fakeSessionExpiration);
    this.lastSessionRecovery = new Date();
    this.storeNewSessionId(sessionData.sessionId);
  }

  getAppMenus(): AppMenus | null {
    return this.appMenus;
  }

  getMenuIconForForm(formName: string): string | null {
    if (!formName || !this.appMenus) {
      return null;
    }
    for (const items of Object.values(this.appMenus)) {
      const icon = this.findMenuItemIcon(items, formName);
      if (icon) {
        return icon;
      }
    }
    return null;
  }

  private findMenuItemIcon(items: MenuItem[], formName: string): string | null {
    for (const item of items ?? []) {
      if (item['form'] === formName && item['icon']) {
        return item['icon'];
      }
      if (item.children?.length) {
        const childIcon = this.findMenuItemIcon(item.children, formName);
        if (childIcon) {
          return childIcon;
        }
      }
    }
    return null;
  }

  userCanNavigate(fullRequestedRoute, requiredFunctions = []) {
    const funRequired = [...(this._componentPageService.pathFunctions(fullRequestedRoute) ?? [])];
    funRequired.push(...requiredFunctions);
    if (!funRequired || funRequired.length === 0) {
      return true;
    }
    const userFunctions = this.userOptions?.userFunctions;
    const hasAllFunctions = userFunctions
      ? funRequired.reduce((hasAll, name) => hasAll && userFunctions.includes(name), true)
      : false;
    return hasAllFunctions;
  }

  filterMenuItems(menuObject, userFunctions) {
    const outputMenu = menuObject.filter(item => this.allFunctionsIncluded(item.functions, userFunctions));
    for (let index = 0; index < outputMenu.length; index++) {
      const menuItem = outputMenu[index];
      if (menuItem.children) {
        menuItem.children = this.filterMenuItems(menuItem.children, userFunctions);
      }
    }
    return outputMenu;
  }

  allFunctionsIncluded(functions, userFunctions) {
    if (functions?.length > 0 && userFunctions?.length > 0) {
      for (let index = 0; index < functions.length; index++) {
        if (!userFunctions.includes(functions[index])) {
          return false;
        }
      }
    }
    return true;
  }

  storeNewSessionId(sessionId) {
    return sessionId && this._storageService.setItem('sessionData', SESSION_ID, { sessionId });
  }

  clearStoredSessionId() {
    return this._storageService.removeItem('sessionData', SESSION_ID);
  }

  getLastRequestedRoute() {
    return this.lastRequestedForm;
  }
  getUserData(): { sessionData: SessionData | null; userOptions: UserOptions | null } {
    return { sessionData: this.sessionData, userOptions: this.userOptions };
  }
  getSessionData(): SessionData | null {
    return this.sessionData;
  }
}
